import { useEffect, useRef, useState, type FormEvent } from "react";
import { Alert, Button, Card, CardBody, Field, Input, Spinner } from "@fellowflats/ui";
import { supabase } from "../lib/supabase";
import { useAuth } from "../lib/auth";
import { Brand } from "../components/brand";

/**
 * The second factor. First visit: set up an authenticator app (scan the QR
 * code, enter a code to confirm it). Every sign-in after: enter a code. The
 * gateway admits staff routes only once this is done (aal2).
 */
export function Factor() {
  const { refresh, signOut } = useAuth();
  const [setup, setSetup] = useState<{ factorId: string; qr: string; secret: string } | null>(null);
  const [factorId, setFactorId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Setting up an authenticator must happen once. React runs effects twice in
  // development, and a second enrol with the same name is refused.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void (async () => {
      const { data, error } = await supabase.auth.mfa.listFactors();
      if (error) return setError(error.message);
      const verified = data.totp.find((f) => f.status === "verified");
      if (verified) return setFactorId(verified.id);
      // Leftover unconfirmed setups would block a new one with the same name.
      for (const f of data.all.filter((f) => f.status === "unverified")) await supabase.auth.mfa.unenroll({ factorId: f.id });
      const enrolled = await supabase.auth.mfa.enroll({ factorType: "totp", friendlyName: "FellowFlats admin" });
      if (enrolled.error) return setError(enrolled.error.message);
      setSetup({ factorId: enrolled.data.id, qr: enrolled.data.totp.qr_code, secret: enrolled.data.totp.secret });
      setFactorId(enrolled.data.id);
    })();
  }, []);

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!factorId) return;
    const code = String(new FormData(e.currentTarget).get("code")).replace(/\s+/g, "");
    setBusy(true);
    setError(null);
    const { error } = await supabase.auth.mfa.challengeAndVerify({ factorId, code });
    setBusy(false);
    if (error) return setError(error.message);
    await refresh();
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-muted px-4">
      <Card className="w-full max-w-sm">
        <CardBody className="space-y-5 p-6">
          <Brand />
          {!factorId && !error && <Spinner />}
          {setup && (
            <div className="space-y-2">
              <p className="text-sm font-medium">Set up your authenticator app</p>
              <p className="text-sm text-muted-foreground">Scan this with Google Authenticator, 1Password or similar, then enter the 6-digit code it shows.</p>
              <img src={setup.qr} alt="QR code for your authenticator app" className="mx-auto h-44 w-44 rounded-md border border-border bg-white p-2" />
              <p className="break-all text-center font-mono text-xs text-muted-foreground">{setup.secret}</p>
            </div>
          )}
          {factorId && (
            <form onSubmit={submit} className="space-y-4">
              <Field label="6-digit code" htmlFor="code">
                <Input id="code" name="code" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9 ]{6,7}" required autoFocus />
              </Field>
              <Button type="submit" fullWidth loading={busy}>
                {setup ? "Confirm and continue" : "Continue"}
              </Button>
            </form>
          )}
          {error && <Alert>{error}</Alert>}
          <Button variant="link" onClick={() => void signOut()}>
            Use another account
          </Button>
        </CardBody>
      </Card>
    </main>
  );
}
